/* ==========================================================================
   Pack Builder — "most-picked first" row ordering (experiment variant)
   --------------------------------------------------------------------------
   Control ships the order the Recharge option-source collections happen to be
   in. That order is not merchandised: it is roughly the order products were
   added to each collection, so the row a visitor meets first opens with
   whatever launched most recently rather than with what people actually pick.
   This file re-orders every visible row so the most-picked products come first.

   ARM. Inert unless sections/pack-builder.liquid has already put
   data-pb-resort="popular-first" on .pb — that attribute IS the variant, set by
   the inline gbComponentGate() call, and evaluating the flag there is the
   exposure. Nothing here reads a flag, so this file can never create one.

   WHAT "MOST-PICKED" MEANS — READ THIS BEFORE TRUSTING THE ORDER.
   RANK below is a combined rank of two independent measurements of the same thing:

     1. ADD-TO-CART CLICKS, from Heatmap.com (site 2250), mobile, per Add button.
        This is the only true per-product add-to-cart signal that exists for this
        store: the builder writes the cart once at submit, so Shopify sees a pack
        and never a tile tap, and GrowthBook's `events` holds only pack_complete
        and builder_scroll.
     2. UNITS SOLD in orders that are NOT subscription renewals, 30d. Renewals are
        75.9% of units and echo a choice made months ago, so including them would
        rank the rows by subscriber inertia.

   They agree: Spearman +0.65 across all 42 products, +0.80 on New Arrivals. Two
   systems that measure different steps of the funnel and share no code arriving at
   the same order is the strongest evidence available that this is signal. Where
   they disagree the combined rank splits the difference, which is the point —
   each carries a bias the other does not.

   HOW THE CLICK NUMBERS WERE OBTAINED, AND WHAT IS WRONG WITH THEM.
   Heatmap identifies elements by CSS path, and for these tiles the path is
   positional (`div:nth-child(N)`), so a click is attributed to a SLOT, not a
   product. Translating slots back to products needs a window in which the row
   layout did not change. Two traps found the hard way:

     - The July NPL cohort (5 products) sat in New Arrivals until 2026-08-31, then
       moved into the front of Gardencups, shifting every slot index behind them.
       Any window spanning that date blends two layouts. Gardencups/Snacks/Produce
       numbers are therefore 2026-09-01..09-15; New Arrivals is 09-08..09-15, the
       only window where that row is a stable 4 slots.
     - ~39% of Add clicks land on a class-signature selector
       (`.pb__product-container.large-cup.selected`) rather than a positional one,
       and cannot be attributed to any product. Those are clicks on a tile that was
       ALREADY in the pack — a second unit of the same cup, which is a different
       action from choosing a product — so excluding them is defensible, but it is
       an exclusion, not a complete count.

   Position bias is real but modest: within the merged Gardencups row, clicks
   correlate with earliness at Spearman +0.43. So ranking on clicks does partly
   re-encode today's order. That is exactly why the order data is mixed in — it is
   measured at a different step and does not share that bias.

   Rank by units, never by revenue: the bundle's price_rule is "dynamic" with
   unit_price 0.00, so component line prices are $0 and every per-product revenue
   number on this store is meaningless. Heatmap's per-element revenue and CVR are
   also unusable for ranking: they attribute the whole session's order to every
   element clicked, n is tiny (7 purchases on 837 clicks for one tile), and
   bottom-of-grid tiles are only ever seen by the deepest-scrolling, highest-intent
   visitors, so their rate is inflated by selection, not merit.

   FROZEN ON PURPOSE. Nothing at runtime can compute this — the Storefront API
   carries no order data and Heatmap has no public read from the storefront — so
   the list is a literal, and it is only honest for the assortment it was cut
   against. Products missing from RANK keep their control position at the end of
   their block rather than being promoted, which means the October NPL cohort
   (publishing 2026-10-02) would land last in New Arrivals. That date is the end of
   this test's clean window.

   TWO PASSES, DELIBERATELY.
     1. Data layer, before render. Patches the two methods that decide product
        order, so tiles are BUILT in rank order. This is what keeps the arms
        comparable on images: renderProduct() gives the first 8 tiles rendered
        (first 2 under 500px) loading="eager" fetchpriority="high" by a running
        counter, so a purely cosmetic re-sort would hand the variant lazy hero
        images and a slower first paint — a confound that reads exactly like a
        merchandising loss.
     2. DOM, after the rows are assembled. pack-builder-override.js mergeSalads()
        concatenates four Recharge collections into the single visible
        "Gardencups" row in a fixed order, so the data-layer pass alone leaves
        four sorted blocks rather than one sorted row. This pass interleaves
        them. It is idempotent, and because pass 1 already put each block in
        order it only ever moves a handful of tiles — never the first nine, so
        the eager set is untouched.

   Tile order is safe to change: quantity, splits and the summary drawer all key
   off [data-product-id]/[data-variant-id]/[data-collection-id], never position
   (.pb__product-container carries no data-index), and .pb__collection-products
   is a plain auto-placed grid, so DOM order is visual order.
   ========================================================================== */
(function () {
  "use strict";

  /* product id -> ordinal, 1 = picked most. Lower sorts first; anything absent
     keeps its control position at the end of its block. The comment on each line
     carries both source measurements and the control slot, so a reviewer can see
     what moved and why without leaving the file. */
  // prettier-ignore
  var RANK = {

    // ---- New Arrivals — rendered from products tagged subscriber_new_product
    15787073503518: 1, // Italian Chopped Salad                      65 clicks |  399 units | was slot 3
    15787077894430: 2, // Dijon Chicken + Wild Rice                  56 clicks |  317 units | was slot 1
    15787076747550: 3, // Angus Beef + Spiced Root Veggies           63 clicks |  264 units | was slot 2
    15787076190494: 4, // Miso Chicken + Quinoa                      37 clicks |  239 units | was slot 4

    // ---- Gardencups — the single merged row (4 Recharge source collections)
    8168015233310: 5, // Caesar w/Grilled Chicken                  454 clicks | 1706 units | was slot 12
    8092057108766: 6, // Chicken Bacon Cobb                        277 clicks | 1576 units | was slot 10
    10053255823646: 7, // Green Goddess w/ Lemon Garlic Chicken     163 clicks | 1521 units | was slot 5
    8168125858078: 8, // Southwest w/Fajita Chicken                171 clicks | 1289 units | was slot 11
    8168080310558: 9, // Asian Chopped w/Honey Garlic Chicken       86 clicks | 1210 units | was slot 18
    15787084513566: 10, // Green Goddess Chicken Pasta               100 clicks | 1141 units | was slot 4
    9089820393758: 11, // Chef Salad w/ Ham & Turkey                 78 clicks |  699 units | was slot 17
    15787070390558: 12, // Herb Chicken + Lemon Orzo                  55 clicks | 1053 units | was slot 3
    9161047769374: 13, // 3-Meat Italian Club                        76 clicks |  695 units | was slot 13
    8168075919646: 14, // Garden Harvest w/Grilled Chicken          121 clicks |  621 units | was slot 9
    9827207020830: 15, // Greek Chicken Kabob Bowl                   49 clicks |  854 units | was slot 6
    9950391861534: 16, // Mike's Hot Honey Chicken Bowl              75 clicks |  680 units | was slot 7
    8168178745630: 17, // Pesto Pasta Bowl w/Grilled Chicken         40 clicks |  717 units | was slot 20
    8168047706398: 18, // Caprese w/Grilled Chicken                  37 clicks |  828 units | was slot 16
    9686301704478: 19, // Steakhouse Wedge                           61 clicks |  618 units | was slot 15
    15787069899038: 20, // Sun-Dried Tomato Chicken Pasta             50 clicks |  669 units | was slot 2
    10053201527070: 21, // Pizza Caesar w/ Crispy Pepperoni           56 clicks |  533 units | was slot 8
    8168102068510: 22, // Mediterranean w/Grilled Chicken            32 clicks |  713 units | was slot 19
    8092056944926: 23, // Southwest Salad                            56 clicks |  281 units | was slot 22
    8092058222878: 24, // Asian Chopped Salad                        39 clicks |  359 units | was slot 25
    9835473568030: 25, // BBQ Chicken Ranch                          33 clicks |  433 units | was slot 14
    15787068653854: 26, // Chipotle Chicken + Couscous                16 clicks |  655 units | was slot 1
    8092058485022: 27, // Pesto Pasta Bowl                           35 clicks |  298 units | was slot 26
    8092058386718: 28, // Garden Harvest Salad                       44 clicks |  103 units | was slot 21
    8092058550558: 29, // Italian Pasta Bowl                         25 clicks |  302 units | was slot 27
    8092058026270: 30, // Caprese Salad                              31 clicks |  293 units | was slot 24
    8092057501982: 31, // Mediterranean Salad                        25 clicks |  240 units | was slot 23

    // ---- Snacks 439682498846
    15787071308062: 32, // Peach Cobbler Overnight Oats              108 clicks |  543 units | was slot 7
    9869321109790: 33, // Apple Crumble Overnight Oats               82 clicks |  325 units | was slot 4
    9899740168478: 34, // Spinach & Feta Egg White Bites             47 clicks |  329 units | was slot 3
    9798957859102: 35, // Berry Parfait                              67 clicks |  306 units | was slot 6
    8096803094814: 36, // Chocolate Strawberry Chia Pudding          62 clicks |  269 units | was slot 5
    10023156908318: 37, // Garden Chicken Salad                       33 clicks |  285 units | was slot 1
    9161061335326: 38, // Tuna Salad                                 38 clicks |  241 units | was slot 2

    // ---- Produce 433236803870
    8168216264990: 39, // Red Apple Slices w/Almond Butter           10 clicks |   94 units | was slot 3
    8154880508190: 40, // Carrots & Celery Sticks w/Hummus            4 clicks |   76 units | was slot 1
    8092144959774: 41, // Pineapple Bites                             5 clicks |   62 units | was slot 2
    8154882179358: 42, // Grapes (seasonal red/green)                 8 clicks |   33 units | was slot 4
  };

  /* The add-ons upsell rail needs no exclusion and gets none. Neither pass can
     reach it: renderCollection() returns early on the add-ons handle so it never
     goes through the sort, and pack-builder-addons.js renders it into
     .pb-summary__addons-list inside the summary drawer, not into a
     .pb__collection-products grid. */

  function rankOf(productId) {
    var value = RANK[String(productId)];
    return typeof value === "number" ? value : Infinity;
  }

  /* Ascending by rank, stable, unranked last. Array.prototype.sort is stable in
     every browser we support, and the comparator returns 0 for ties on purpose so
     the whole unranked tail keeps its control order rather than being shuffled
     arbitrarily between arms. Infinity - Infinity is NaN, which would make the
     comparator incoherent and the sort order implementation-defined, so unranked
     pairs are compared explicitly. */
  function byRankAsc(a, b) {
    var ra = rankOf(a),
      rb = rankOf(b);
    if (ra === rb) return 0;
    return ra < rb ? -1 : 1;
  }

  function isActive() {
    var root = document.querySelector(".pb");
    return !!root && root.getAttribute("data-pb-resort") === "popular-first";
  }

  /* ------------------------------------------------------- pass 1: data layer

     Both patches run before a single tile exists, so the eager-image counter,
     the rendered order and the DOM all agree. They delegate to the original
     implementation rather than replacing it: promotional_product_position_index
     still pins promoted products to their slot, and everything else fills the
     gaps in rank order instead of catalog order. */
  function patchPrototype() {
    if (typeof PackBuilder === "undefined" || !PackBuilder.prototype) return false;
    if (PackBuilder.prototype.__pbResortPatched) return true;
    PackBuilder.prototype.__pbResortPatched = true;

    var originalSort = PackBuilder.prototype.sortProductsByPromotionalPosition;
    if (typeof originalSort === "function") {
      PackBuilder.prototype.sortProductsByPromotionalPosition = function (products, collectionId) {
        if (!isActive() || !Array.isArray(products)) {
          return originalSort.call(this, products, collectionId);
        }
        return originalSort.call(this, products.slice().sort(byRankAsc), collectionId);
      };
    }

    /* New Arrivals never goes through the sort above — renderSubscriberNewProductsBlock()
       renders getSubscriberNewProductIds() verbatim — so it needs its own patch.
       Both the block and renderCollections() call this, and sorting is
       idempotent, so patching the source covers every caller. */
    var originalNewIds = PackBuilder.prototype.getSubscriberNewProductIds;
    if (typeof originalNewIds === "function") {
      PackBuilder.prototype.getSubscriberNewProductIds = function () {
        var ids = originalNewIds.call(this);
        if (!isActive() || !Array.isArray(ids)) return ids;
        return ids.slice().sort(byRankAsc);
      };
    }

    return true;
  }

  /* -------------------------------------------------------------- pass 2: DOM

     Runs after pack-builder-override.js applyCollectionManagement() has merged
     the four salad collections into one row, which it does inside a bare
     setTimeout(…, 100) that we cannot hook — hence an observer plus a deadline
     rather than a single delayed call. */
  function tileId(container) {
    var card = container.querySelector("[data-product-id]");
    return card ? card.getAttribute("data-product-id") : null;
  }

  /* Returns true if it moved anything, so the caller can tell a settled DOM from
     a busy one. Reads the whole row, sorts, and only writes when the order
     actually differs — a no-op pass must not touch the DOM or the observer
     would feed itself forever. */
  function sortRow(list) {
    var tiles = Array.prototype.slice.call(list.children).filter(function (el) {
      return el.classList && el.classList.contains("pb__product-container");
    });
    if (tiles.length < 2) return false;

    var sorted = tiles.slice().sort(function (a, b) {
      return byRankAsc(tileId(a), tileId(b));
    });

    var changed = sorted.some(function (el, i) {
      return el !== tiles[i];
    });
    if (!changed) return false;

    /* One fragment, one insertion: re-appending nodes one by one would lay out
       and paint an intermediate order on a 27-tile row. Moving a node keeps its
       listeners and its state, so nothing needs re-wiring after this. */
    var fragment = document.createDocumentFragment();
    sorted.forEach(function (el) {
      fragment.appendChild(el);
    });
    list.appendChild(fragment);
    return true;
  }

  function sortAllRows() {
    var moved = false;
    document.querySelectorAll(".pb__collection-products").forEach(function (list) {
      if (sortRow(list)) moved = true;
    });
    return moved;
  }

  function watchDom() {
    var container = document.querySelector(".pb__collections");
    if (!container) return;

    var busy = false;
    var scheduled = false;

    function run() {
      scheduled = false;
      busy = true;
      sortAllRows();
      /* Release only after the mutations we just caused have been delivered,
         so our own writes cannot re-trigger the callback. */
      requestAnimationFrame(function () {
        busy = false;
      });
    }

    var observer = new MutationObserver(function () {
      if (busy || scheduled) return;
      scheduled = true;
      requestAnimationFrame(run);
    });

    observer.observe(container, { childList: true, subtree: true });
    run();

    /* The grid is finished long before this; the deadline is only here so a
       page that keeps mutating for some unrelated reason cannot leave an
       observer running for the life of the session. */
    setTimeout(function () {
      observer.disconnect();
      sortAllRows();
    }, 8000);
  }

  if (!isActive()) return;
  patchPrototype();

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", watchDom);
  } else {
    watchDom();
  }
})();
